import React, { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Tag from '@rio-cloud/rio-uikit/Tag';

import { formatRange } from '@/features/common/components/RangeInputWithSlider';
import { FILTER_MSG } from '@/features/maintenance-data/filters/constants';
import { ComponentType, MaintenanceStatus } from '@/generated/GeneratedApiTypes';

const STATUS_LABEL_KEY: Record<MaintenanceStatus, string> = {
    [MaintenanceStatus.OVERDUE]: `${FILTER_MSG}.status.overdue`,
    [MaintenanceStatus.DUE]: `${FILTER_MSG}.status.almostDue`,
    [MaintenanceStatus.NEXT]: `${FILTER_MSG}.status.next`,
    [MaintenanceStatus.UNKNOWN]: `${FILTER_MSG}.status.unknown`,
};

const COMPONENT_TYPE_LABEL_KEY: Record<ComponentType, string> = {
    [ComponentType.MAINTENANCE_COMPONENT]: `${FILTER_MSG}.component-type.vehicle`,
    [ComponentType.CUSTOM_COMPONENT]: `${FILTER_MSG}.component-type.custom`,
};

const componentSlugLabelKey = (slug: string) =>
    `intl-msg:vehicle-diagnostics.maintenance.component.${slug.replaceAll('_', '-')}`;

export interface SelectedFilterTagsProps {
    status: MaintenanceStatus[];
    onRemoveStatus: (status: MaintenanceStatus) => void;
    solvedByCustomer: boolean;
    onRemoveSolvedByCustomer: () => void;
    componentType: ComponentType[];
    onRemoveComponentType: (type: ComponentType) => void;
    component: string[];
    onRemoveComponent: (slug: string) => void;
    dueDateFrom?: string;
    dueDateTo?: string;
    onRemoveDueAt: () => void;
    dueMileageMin?: number;
    dueMileageMax?: number;
    onRemoveDueIn: () => void;
    mileageMin?: number;
    mileageMax?: number;
    onRemoveMileage: () => void;
    dueRangesOrGate: boolean;
    onRemoveDueRangesOrGate: () => void;
    onResetAll: () => void;
}

interface TagDescriptor {
    key: string;
    label: ReactNode;
    onRemove: () => void;
}

const SelectedFilterTags: React.FC<SelectedFilterTagsProps> = (props) => {
    const intl = useIntl();
    const formatDate = (iso: string) =>
        intl.formatDate(new Date(iso), { year: 'numeric', month: 'long', day: 'numeric' });

    const tags: TagDescriptor[] = [];

    if (props.dueDateFrom || props.dueDateTo) {
        const from = props.dueDateFrom ? formatDate(props.dueDateFrom) : '';
        const to = props.dueDateTo ? formatDate(props.dueDateTo) : '';
        tags.push({
            key: 'dueAt',
            label: <FormattedMessage id={`${FILTER_MSG}.tag.dueAt`} values={{ value: `${from} - ${to}` }} />,
            onRemove: props.onRemoveDueAt,
        });
    }

    if (typeof props.dueMileageMin === 'number' || typeof props.dueMileageMax === 'number') {
        tags.push({
            key: 'dueIn',
            label: (
                <FormattedMessage
                    id={`${FILTER_MSG}.tag.dueIn`}
                    values={{ value: formatRange({ from: props.dueMileageMin, to: props.dueMileageMax }, 'km') }}
                />
            ),
            onRemove: props.onRemoveDueIn,
        });
    }

    if (props.dueRangesOrGate) {
        tags.push({
            key: 'orGate',
            label: <FormattedMessage id={`${FILTER_MSG}.tag.dueRangesOperator`} />,
            onRemove: props.onRemoveDueRangesOrGate,
        });
    }

    props.status.forEach((s) => {
        tags.push({
            key: `status-${s}`,
            label: (
                <FormattedMessage
                    id={`${FILTER_MSG}.tag.status`}
                    values={{ value: intl.formatMessage({ id: STATUS_LABEL_KEY[s] }) }}
                />
            ),
            onRemove: () => props.onRemoveStatus(s),
        });
    });

    if (props.solvedByCustomer) {
        tags.push({
            key: 'solvedByCustomer',
            label: (
                <FormattedMessage
                    id={`${FILTER_MSG}.tag.status`}
                    values={{ value: intl.formatMessage({ id: `${FILTER_MSG}.status.solvedByCustomer` }) }}
                />
            ),
            onRemove: props.onRemoveSolvedByCustomer,
        });
    }

    props.componentType.forEach((t) => {
        tags.push({
            key: `type-${t}`,
            label: (
                <FormattedMessage
                    id={`${FILTER_MSG}.tag.componentType`}
                    values={{ value: intl.formatMessage({ id: COMPONENT_TYPE_LABEL_KEY[t] }) }}
                />
            ),
            onRemove: () => props.onRemoveComponentType(t),
        });
    });

    props.component.forEach((slug) => {
        tags.push({
            key: `component-${slug}`,
            label: (
                <FormattedMessage
                    id={`${FILTER_MSG}.tag.component`}
                    values={{
                        value: intl.formatMessage({ id: componentSlugLabelKey(slug), defaultMessage: slug }),
                    }}
                />
            ),
            onRemove: () => props.onRemoveComponent(slug),
        });
    });

    if (typeof props.mileageMin === 'number' || typeof props.mileageMax === 'number') {
        tags.push({
            key: 'mileage',
            label: (
                <FormattedMessage
                    id={`${FILTER_MSG}.tag.mileage`}
                    values={{ value: formatRange({ from: props.mileageMin, to: props.mileageMax }, 'km') }}
                />
            ),
            onRemove: props.onRemoveMileage,
        });
    }

    if (tags.length === 0) {
        return <div className="margin-bottom-20" data-testid="selected-filter-tags-empty" />;
    }

    return (
        <div className="selected-filter-tags display-flex flex-wrap gap-5 align-items-center margin-top-10">
            {tags.map((tag) => (
                <Tag key={tag.key} size="small" deletable clickable onClick={tag.onRemove}>
                    {tag.label}
                </Tag>
            ))}
            <button type="button" className="btn btn-link btn-xs" onClick={props.onResetAll}>
                <span className="rioglyph rioglyph-trash margin-right-5" />
                <FormattedMessage id={`${FILTER_MSG}.deleteAll`} />
            </button>
        </div>
    );
};

export default SelectedFilterTags;
